import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v7 as uuidV7 } from "uuid";
import { deleteMessageFeedback, submitMessageFeedback } from "@/actions/gailgpt/feedback/actions";
import { Assistant as ChatAssistant } from "@/components/chat";
import { usePageContext } from "@/contexts/chat/PageContextContext";
import { useAuthContext } from "@/contexts/common/AuthProvider";
import type { OptimisticMessage } from "@/contexts/common/ChatTransferContextProvider";
import { useAlert } from "@/hooks/useAlert";
import { useAssistants } from "@/hooks/useAssistants";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useOrg } from "@/hooks/useOrg";
import {
  accumulateStreamEvent,
  appendContent,
  ConversationDetailsResponse,
  discoverConversationRouting,
  rankConversationHistory,
  EntitlementContext,
  GENERATION_TIMED_OUT_STATUS,
  ConversationMessage,
  isMetadataEvent,
  isTransientEvent,
  MessageContentFunctionCall,
  MessageContentFunctionResult,
  MessageContentMetadata,
  pinConversationRouting,
  resolveConversationRouting,
  conversationHasWorkflowTools,
  isAutoSelectableForConversation,
  StreamingConversationMessage,
  ToolApprovalResponse,
  WingmanApiError,
  WingmanEventStream,
} from "@/services/wingman";
import { WingmanGenerationError } from "@/services/wingman/generation-error";
import { collectOmegaPresentation, isOmegaToolPart } from "@/services/wingman/omega-results";
import { useWorkflowTurns, type WorkflowAction, type WorkflowDebugAction } from "@/hooks/useWorkflowTurns";
import { useWingmanRouting } from "@/hooks/useWingmanRouting";
import { captureWingmanConversationFlow } from "@/lib/wingman-tracing";
import { isAutoAssistantModel } from "@/utils/ai/modelCatalog";
import { FeedbackCategory, FeedbackRating } from "@/types/feedback";
import {
  getMessageClient,
  handleWingmanFailure,
} from "@/utils/chat/messageClient";
import { MessageProcessingContext } from "@/utils/chat/messageProcessing";
import { TokenBreakdown } from "@/utils/chat/tokenEstimation";
import {
  assistantFailureAlert,
  assistantFailureText,
  classifyAssistantFailure,
} from "@/utils/chat/tokenLimitError";
import {
  buildSendPayload,
  convertSingleMessage,
  createAssistantMessage,
  extractErrorPayload,
  fetchFeedbackMap,
  getSafeAcknowledgment,
} from "@/utils/chat/useChatUtils";
import {
  MIME_GPT_DEFAULT,
  applyGailGptLimits,
  getIncompatibleFilesAsync,
} from "@/utils/fileHelpers";
import {groupApprovalContinuations} from "@/utils/chat/groupApprovalContinuations";

const EMPTY_BREAKDOWN: TokenBreakdown = { conversation: 0, system: 0, attachments: 0 };

const isDeniedToolResult = (result: unknown): boolean =>
  typeof result === "object" && result !== null && "denied" in result && result.denied === true;

const withApprovalDecision = (
  contents: ConversationMessage["contents"],
  callId: string,
  approved: boolean
): ConversationMessage["contents"] =>
  contents.map((content) =>
    content.type === "function_call" && content.callId === callId && content.approval
      ? {...content, approval: {...content.approval, approved}}
      : content
  );

/** Fold a continuation call/result into the original approval proposal message. */
const applyApprovalContinuation = (
  item: ConversationMessage,
  approvalProposalId: string,
  data: MessageContentFunctionCall | MessageContentFunctionResult
): ConversationMessage => {
  if (item.id !== approvalProposalId) return item;
  const updated = accumulateStreamEvent(item, data);
  if (data.type !== "function_result") return updated;
  return {
    ...updated,
    contents: withApprovalDecision(updated.contents, data.callId, !isDeniedToolResult(data.result)),
  };
};

/**
 * True when an error is the expected fallout of the user pressing stop:
 * an aborted fetch (pre-response, wrapped by the client), an aborted stream
 * read (mid-flight), or a server-reported cancelled generation (v2).
 */
const isStreamCancellation = (error: unknown): boolean => {
  if (error instanceof WingmanApiError) return error.kind === "aborted";
  if (error instanceof WingmanGenerationError) return error.kind === "Cancelled";
  // DOMException does not extend Error in every runtime (jsdom), so check it
  // first — same pattern as the client's isAbortError.
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    return error.name === "AbortError";
  }
  return error instanceof Error && error.name === "AbortError";
};

interface UseChatProps {
  orgId?: string;
  conversationId?: string;
  initialAssistantId?: string | null;
  isNewConversation?: boolean;
  optimisticMessage?: OptimisticMessage | null;
  attachPageContext?: boolean;
  /** Saved workflow that ordinary turns refer to (v2 only). */
  selectedWorkflowId?: string;
  /** Runs before each v2 turn; resolving false refuses the turn. */
  prepareWorkflowTurn?: () => Promise<boolean>;
}

export const useChat = ({
  orgId,
  conversationId,
  initialAssistantId,
  optimisticMessage = null,
  attachPageContext = false,
  selectedWorkflowId,
  prepareWorkflowTurn,
}: UseChatProps) => {
  const { setAlert } = useAlert();
  const { user } = useAuthContext();

  const { t } = useTranslation("ask");

  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [fileCompatibilityIssue, setFileCompatibilityIssue] = useState(false);
  const [isOptimisticConversation, setIsOptimisticConversation] = useState(false);
  const [canSendMessages, setCanSendMessages] = useState(true);
  const [conversationOwnerId, setConversationOwnerId] = useState<string | null>(null);
  const [ownerConversationId, setOwnerConversationId] = useState<string | null>(null);
  const [isWaitingForToolResponse, setIsWaitingForToolResponse] = useState(false);
  const [tokenLimitReached, setTokenLimitReached] = useState(false);
  const tokenLimitReachedRef = useRef(false);
  const [tokenUsagePercent, setTokenUsagePercent] = useState(0);
  const [tokenBreakdown, setTokenBreakdown] = useState<TokenBreakdown>(EMPTY_BREAKDOWN);
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const processedOptimisticMessageRef = useRef<string | null>(null);
  const streamAbortControllerRef = useRef<AbortController | null>(null);

  const cancelStream = useCallback(() => {
    streamAbortControllerRef.current?.abort();
  }, []);

  const { getPageContext } = usePageContext();

  const resolvePageContext = useCallback(() => {
    if (!attachPageContext) return undefined;
    return getPageContext() ?? undefined;
  }, [attachPageContext, getPageContext]);

  const { org, isGailGptOnlyOrg } = useOrg();

  // Advisory only — Wingman gates authoritatively against Galactus (see EntitlementContext).
  const resolveEntitlementContext = useCallback(
    (): EntitlementContext => ({
      products: (org.products ?? []).map((product) => ({
        name: product.name,
        subscriptionStatus: product.subscriptionStatus,
      })),
      isGailGptOnly: isGailGptOnlyOrg(),
    }),
    [org.products, isGailGptOnlyOrg]
  );

  const {
    selectedAssistant,
    isLoadingAssistants,
    displayAssistants,
    displaySelectedAssistant,
    handleAssistantChange: handleAssistantChangeBase,
    updateAssistantData,
    getAuthenticatedClient,
    getFileUploadConfig,
    fetchAssistants,
    hasFetchError,
  } = useAssistants({
    initialAssistantId,
    autoFetch: true,
  });
  const wingmanRouting = useWingmanRouting(selectedAssistant?.model);
  const resolveRoutingForConversation = (id: string) =>
    resolveConversationRouting(id, selectedAssistant?.model);

  const workflowPresentation = useMemo(() => collectOmegaPresentation(messages), [messages]);
  const workflowTurns = useWorkflowTurns({
    available: !!conversationId && conversationHasWorkflowTools(conversationId, selectedAssistant?.model),
    selectedWorkflowId: selectedWorkflowId ?? workflowPresentation.defaultWorkflowId,
    prepareWorkflowTurn,
    listHistory: orgId && conversationId
      ? async () => {
        const client = await getAuthenticatedClient(resolveRoutingForConversation(conversationId));
        return client.conversations.listConversationMessages({xOrgId: orgId, conversationId});
      }
      : null,
    setMessages,
  });

  const fileUploadConfig = applyGailGptLimits(getFileUploadConfig(), selectedAssistant?.model);

  const {
    attachedFiles,
    handleFilesSelected,
    handleFilesChange,
    handleInvalidFiles,
    clearAttachedFiles,
    getFileExtensionFromMimeType,
  } = useFileUpload(fileUploadConfig, selectedAssistant?.model);

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  useEffect(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      // Conversation switches remount chat (`key={conversationId}`); abort so
      // the prior stream can't keep generating / writing into a dead tree.
      streamAbortControllerRef.current?.abort();
      streamAbortControllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (attachedFiles.length === 0) {
      setFileCompatibilityIssue(false);
    }
  }, [attachedFiles.length]);

  // Check if current user can send messages in this conversation
  const checkConversationOwnership = async () => {
    if (!conversationId) return;

    try {
      const currentUserId = user?.userId ?? null;
      if (!currentUserId) return;

      // If we already have the conversation owner ID, use it
      if (conversationOwnerId && ownerConversationId === conversationId) {
        setCanSendMessages(conversationOwnerId === currentUserId);
        return;
      }

      // Otherwise, fetch conversation details to get the owner
      try {
        const conversationDetails = await getConversationDetails();
        const userId = conversationDetails?.userId;
        if (userId) {
          setConversationOwnerId(userId);
          setOwnerConversationId(conversationId);
          setCanSendMessages(userId === currentUserId);
        }
      } catch (error) {
        console.error("Error checking conversation ownership:", error);
        // If we can't determine ownership, allow sending (default behavior)
        setCanSendMessages(true);
      }
    } catch (error) {
      console.error("Error getting current user ID:", error);
      // If we can't get user ID, allow sending (default behavior)
      setCanSendMessages(true);
    }
  };

  useEffect(() => {
    checkConversationOwnership();
  }, [conversationId, conversationOwnerId, ownerConversationId, user?.userId]);

  useEffect(() => {
    if (!optimisticMessage || isOptimisticConversation) {
      return;
    }

    const messageKey = `${optimisticMessage.text}-${optimisticMessage.fileData.length}-${optimisticMessage.assistantId}`;

    if (processedOptimisticMessageRef.current === messageKey) {
      return;
    }

    let isActive = true;

    // Defer the send until after React has completed any Strict Mode effect
    // replay. The replay cleanup cancels the first scheduled attempt, leaving
    // the live effect to start exactly one request with a usable abort signal.
    queueMicrotask(() => {
      if (!isActive || processedOptimisticMessageRef.current === messageKey) {
        return;
      }

      processedOptimisticMessageRef.current = messageKey;

      setIsOptimisticConversation(true);

      const baseTimestamp = new Date();
      const optimisticMessages: ConversationMessage[] = [];

      // If there's text, use it; otherwise use a default message when only files are attached
      const messageContent = optimisticMessage.text.trim()
        ? optimisticMessage.text
        : t("defaultFileMessage", { count: optimisticMessage.fileData.length });

      optimisticMessages.push({
        id: uuidV7(),
        contents: [
          {
            type: "text",
            content: messageContent,
          },
        ],
        role: "user",
        author: "self",
        timestamp: baseTimestamp,
      });

      optimisticMessage.fileData.forEach((file, index) => {
        optimisticMessages.push({
          id: uuidV7(),
          contents: [
            {
              type: "binary",
              data: file.data,
              mimeType: file.type,
              fileName: file.name,
            },
          ],
          role: "user",
          author: "self",
          timestamp: new Date(baseTimestamp.getTime() + index + 1),
        });
      });

      setMessages(optimisticMessages);
      setIsSubmitting(true);

      handleOptimisticConversation(optimisticMessage);
    });

    return () => {
      isActive = false;
    };
  }, [optimisticMessage]);

  const handleAssistantChange = async (assistant: ChatAssistant) => {
    if (assistant.id === "loading") {
      return;
    }

    const newFileUploadConfig = applyGailGptLimits(
      {
        enabled: assistant.fileUpload?.enabled ?? false,
        acceptedFileTypes: MIME_GPT_DEFAULT.join(","),
        maxFileCount: assistant.fileUpload?.maxFileCount ?? 0,
      },
      assistant.model
    );
    const incompatibleFiles = await getIncompatibleFilesAsync(
      attachedFiles,
      newFileUploadConfig,
      assistant.model
    );

    if (incompatibleFiles.length > 0) {
      setFileCompatibilityIssue(true);
      const fileNames = incompatibleFiles.map((f) => f.file.name).join(", ");
      const alertMessage = t("incompatibleFilesDetected", {
        assistantName: assistant.name,
        fileCount: incompatibleFiles.length,
        fileNames: fileNames,
      });

      setAlert({
        status: "warning",
        message: alertMessage,
        secondsToClose: 10,
      });
    } else {
      setFileCompatibilityIssue(false);
    }

    handleAssistantChangeBase(assistant);
  };

  const updateMessagesWithMessage = (message: ConversationMessage) => {
    setMessages((prevMessages) =>
      prevMessages.some((msg) => msg.id === message.id)
        ? prevMessages.map((msg) => (msg.id === message.id ? message : msg))
        : [...prevMessages, message]
    );
  };

  const handleMetadataEvent = (eventData: MessageContentMetadata): ConversationMessage => {
    const message: ConversationMessage = {
      id: eventData.id,
      contents: [],
      role: eventData.role,
      author: "assistant",
      timestamp: eventData.timestamp,
    };

    setStreamingMessageId(eventData.id);
    setMessages((prevMessages) => [...prevMessages, message]);
    return message;
  };

  /** Handle transient SSE events (token_usage, notification). */
  const handleTransientEvent = (data: StreamingConversationMessage): void => {
    if (data.type === "token_usage") {
      setTokenUsagePercent(data.usagePercent);
      setTokenLimitReached(data.limitReached);
      tokenLimitReachedRef.current = data.limitReached;
      setTokenBreakdown(data.breakdown ?? EMPTY_BREAKDOWN);
      return;
    }
    if (data.type === "notification") {
      const msg = data.key ? t(data.key, data.params ?? {}) : data.message;
      setAlert({ status: "info", message: msg, secondsToClose: 10 });
    }
  };

  /**
   * Resolve a `function_result` into the (possibly augmented) message that
   * should be folded into the stream. If the tool has a safe acknowledgment
   * and the assistant hasn't said anything yet, add that ack as a text
   * content item before the `function_result` is accumulated.
   * `accumulateStreamEvent` will then append the result itself, preserving
   * the intended ordering (ack text → function_result).
   */
  const applyFunctionResultAck = (
    eventData: MessageContentFunctionResult,
    message: ConversationMessage
  ): ConversationMessage => {
    const functionName = eventData.functionName || "tool";
    const safeAck = getSafeAcknowledgment(functionName, t);
    setIsWaitingForToolResponse(!safeAck);

    const hasAnyAssistantText = message.contents.some(
      (c) =>
        c.type === "text" &&
        "content" in c &&
        typeof c.content === "string" &&
        c.content.trim().length > 0
    );

    if (!hasAnyAssistantText && safeAck) {
      return appendContent(message, { type: "text", content: safeAck });
    }
    return message;
  };

  /** Run any UI-state side effects for an incoming event and return the message
   *  the reducer should fold into next (may be augmented for function_result). */
  const applyEventSideEffects = (
    data: StreamingConversationMessage,
    message: ConversationMessage
  ): ConversationMessage => {
    if (data.type === "text") {
      setIsSubmitting(false);
      setIsWaitingForToolResponse(false);
      return message;
    }
    if (data.type === "function_call") {
      setIsWaitingForToolResponse(!data.approval);
      return message;
    }
    if (data.type === "function_result") {
      setIsSubmitting(false);
      return applyFunctionResultAck(data as MessageContentFunctionResult, message);
    }
    return message;
  };

  const handleStreamStatus = (content: string | undefined | null): boolean => {
    if (content !== GENERATION_TIMED_OUT_STATUS) {
      setStreamingStatus(content ?? null);
      return false;
    }
    // A timeout ends the stream, so keep it visible after streaming UI clears.
    setStreamingStatus(null);
    setIsWaitingForToolResponse(false);
    const msg = t("status.generation_timed_out");
    setMessages((prev) => [...prev, createAssistantMessage(msg)]);
    setAlert({ status: "error", message: msg, secondsToClose: 10 });
    return true;
  };

  const applyTransientEvent = (
    data: StreamingConversationMessage,
    message: ConversationMessage | null
  ): ConversationMessage | null => {
    if (data.type === "recalled_memories" && message !== null) {
      message = { ...message, recalledFacts: data.facts };
      updateMessagesWithMessage(message);
    }
    handleTransientEvent(data);
    return message;
  };

  const routeApprovalContinuation = (
    data: StreamingConversationMessage,
    proposal?: ConversationMessage
  ): boolean => {
    if (!proposal || (data.type !== "function_call" && data.type !== "function_result")) return false;
    const belongsToProposal = proposal.contents.some((content) =>
      content.type === "function_call" && content.approval && content.callId === data.callId
    );
    if (!belongsToProposal) return false;
    setMessages((previous) =>
      previous.map((item) => applyApprovalContinuation(item, proposal.id, data))
    );
    return true;
  };

  const applyMessageEvent = (
    data: StreamingConversationMessage,
    message: ConversationMessage
  ): ConversationMessage => {
    // Drop leaked text while retaining confirmed Omega saves and tests.
    // The server persists the same terminal marker for subsequent reloads.
    if (data.type === "generation_failed") {
      setStreamingStatus(null);
      setIsWaitingForToolResponse(false);
      return { ...message, contents: [...message.contents.filter(isOmegaToolPart), data] };
    }
    // Clear status once real (non-thinking) content arrives.
    if (data.type !== "thinking") setStreamingStatus(null);
    return accumulateStreamEvent(applyEventSideEffects(data, message), data);
  };

  const processResponse = async (
    response: WingmanEventStream<StreamingConversationMessage>,
    approvalProposal?: ConversationMessage
  ): Promise<boolean> => {
    let interrupted = false;
    let message: ConversationMessage | null = null;

    for await (const event of response) {
      const { data } = event;

      const errorPayload = extractErrorPayload(data);
      if (errorPayload) {
        interrupted = true;
        classifyAndHandleError(errorPayload);
        setIsWaitingForToolResponse(false);
        break;
      }

      if (data.type === "status") {
        if (handleStreamStatus(data.content)) {
          interrupted = true;
          break;
        }
        continue;
      }

      if (isTransientEvent(data)) {
        message = applyTransientEvent(data, message);
        continue;
      }

      if (isMetadataEvent(data)) {
        message = handleMetadataEvent(data);
        continue;
      }

      // Continuation results belong to the earlier proposal, not the new reply.
      // They can arrive before the new assistant's metadata or text.
      if (routeApprovalContinuation(data, approvalProposal) || message === null) continue;

      interrupted ||= data.type === "generation_failed";
      message = applyMessageEvent(data, message);
      updateMessagesWithMessage(message);
    }

    setStreamingMessageId(null);
    setStreamingStatus(null);
    return interrupted;
  };

  /**
   * Shared error classifier — used by both SSE error events and catch handlers.
   * Returns `true` if the error was silently handled (e.g. de-duplicated token
   * limit), so callers can decide whether to log.
   *
   * Race-condition note: A `token_usage` SSE event with `limitReached: true` can
   * arrive before a subsequent error event in the same stream. Because React batches
   * `setTokenLimitReached`, the state value would still be `false` when this function
   * runs. The ref (`tokenLimitReachedRef`) is set synchronously to avoid this stale-
   * closure issue and reliably de-duplicate error handling.
   */
  const classifyAndHandleError = (error: unknown): boolean => {
    const failure = classifyAssistantFailure(error);
    if (tokenLimitReachedRef.current && failure.kind === "token_limit") return true;

    if (failure.kind === "token_limit") {
      setTokenLimitReached(true);
      tokenLimitReachedRef.current = true;
    }
    const text = assistantFailureText(failure, t);
    setMessages((prev) => [...prev, createAssistantMessage(text)]);
    setAlert({ status: "error", message: assistantFailureAlert(failure, t), secondsToClose: 10 });
    return false;
  };

  const handleMessageError = (error: unknown, errorType: "optimistic" | "regular") => {
    const silentlyHandled = classifyAndHandleError(error);
    if (!silentlyHandled) {
      const logPrefix =
        errorType === "optimistic" ? "Error in optimistic conversation:" : "Error sending message:";
      // In development, log only the message: Next.js turns `console.error(Error)` into the dev
      // console overlay. Everywhere else, log the full Error so production logs keep the stack.
      console.error(
        logPrefix,
        process.env.NODE_ENV === "development" && error instanceof Error ? error.message : error
      );
    }
  };

  const clearStreamingUiState = () => {
    setStreamingMessageId(null);
    setStreamingStatus(null);
    setIsWaitingForToolResponse(false);
  };

  /**
   * Runs a send under a user-cancellable stream: registers the abort
   * controller so `cancelStream` can interrupt it, treats aborts as
   * user-initiated (no error surfaces), and always releases the controller
   * and streaming/submitting UI state afterwards.
   *
   * Re-entrancy: refuses to start when a controller is already registered.
   * `isSubmitting` alone is insufficient — it clears on the first streamed
   * text token, so programmatic sends mid-stream would otherwise clobber the
   * shared controller and leave the original stream uncancellable.
   *
   * On user cancel, the accumulated partial assistant reply is kept in local
   * state. Wingman v2 discards that partial server-side (no `generation.commit`),
   * so it is UI-only until a future reload.
   */
  const runCancellableSend = async (
    send: (signal: AbortSignal) => Promise<void>,
    errorType: "optimistic" | "regular",
    onSettled?: () => void,
    reconcileOnFailure = false
  ) => {
    if (streamAbortControllerRef.current) {
      return;
    }

    const abortController = new AbortController();
    streamAbortControllerRef.current = abortController;

    let reconciled = false;
    try {
      await send(abortController.signal);
    } catch (error) {
      if (reconcileOnFailure) {
        if (!isStreamCancellation(error)) workflowTurns.reportFailure(error);
        await workflowTurns.reconcile();
        reconciled = true;
      }
      if (!isStreamCancellation(error)) {
        handleWingmanFailure(error, conversationId);
        handleMessageError(error, errorType);
      }
      // Cancellations stay quiet (no alert / "sorry" / failure tracing).
      // Streaming UI is cleared in `finally` for cancel, error, and success.
    } finally {
      if (reconcileOnFailure && !reconciled && abortController.signal.aborted) await workflowTurns.reconcile();
      if (streamAbortControllerRef.current === abortController) {
        streamAbortControllerRef.current = null;
      }
      setIsSubmitting(false);
      clearStreamingUiState();
      onSettled?.();
    }
  };

  const handleOptimisticConversation = async (optimisticMessage: OptimisticMessage) => {
    if (!orgId) {
      setIsSubmitting(false);
      return;
    }

    if (isSubmitting || streamAbortControllerRef.current) {
      // The optimistic-message effect already set isSubmitting +
      // isOptimisticConversation before calling. Rejecting without clearing
      // them freezes the composer: isOptimisticConversation blocks
      // handleSendMessage and is otherwise only cleared on send settle.
      setIsSubmitting(false);
      setIsOptimisticConversation(false);
      return;
    }

    const hasAttachments = optimisticMessage.fileData.length > 0;
    const currentConversationId = conversationId;
    const workflowConversation = workflowTurns.available;
    if (workflowConversation && !(await workflowTurns.prepareTurn())) {
      setIsSubmitting(false);
      setIsOptimisticConversation(false);
      return;
    }

    await runCancellableSend(
      async (signal) => {
        const routing = currentConversationId
          ? resolveRoutingForConversation(currentConversationId)
          : undefined;
        const client = await getMessageClient({ hasAttachments, routing });

        if (!currentConversationId) {
          throw new Error("No conversation ID available");
        }

        const contents: any[] = [];

        // If there's text, use it; otherwise use a default message when only files are attached
        const messageText = optimisticMessage.text.trim()
          ? optimisticMessage.text
          : t("defaultFileMessage", { count: optimisticMessage.fileData.length });
        const messageContent = workflowTurns.ordinaryText(messageText);

        contents.push({
          type: "text",
          content: messageContent,
        });

        for (const file of optimisticMessage.fileData) {
          contents.push({
            type: "binary",
            data: file.data,
            mimeType: file.type,
            fileName: file.name,
          });
        }

        const response = await client.conversations.appendConversationMessage(
          {
            xOrgId: orgId,
            conversationId: currentConversationId,
            appendConversationMessageRequest: {
              contents,
              ...((routing ?? wingmanRouting).apiVersion === "v2" ? {approvalPresentation: "native" as const} : {}),
              pageContext: resolvePageContext(),
              entitlementContext: resolveEntitlementContext(),
            },
          },
          { signal }
        );
        pinConversationRouting(currentConversationId, routing ?? wingmanRouting);
        captureWingmanConversationFlow({
          action: "send",
          conversationId: currentConversationId,
          routing: routing ?? wingmanRouting,
        });

        const interrupted = await processResponse(response);
        if (workflowConversation && interrupted) await workflowTurns.reconcile();
      },
      "optimistic",
      () => setIsOptimisticConversation(false),
      workflowConversation
    );
  };

  const resolveSendText = (
    rawText: string,
    approval?: ToolApprovalResponse,
    action?: WorkflowAction
  ): string => {
    if (approval) return "";
    if (action) return workflowTurns.actionText(action);
    return workflowTurns.ordinaryText(rawText);
  };

  /**
   * Resolves `false` when the send was refused up front (another stream is
   * active, no assistant, token limit, ...) and `true` once an accepted send
   * has run to completion. Programmatic senders use the refusal to keep their
   * message instead of losing it.
   */
  const sendMessage = async ({messageText, toolApprovalResponse, workflowAction}: {
    messageText?: string;
    toolApprovalResponse?: ToolApprovalResponse;
    workflowAction?: WorkflowAction;
  }): Promise<boolean> => {
    const workflowConversation = workflowTurns.available;
    const rawText = messageText || inputValue;
    const textToSend = resolveSendText(rawText, toolApprovalResponse, workflowAction);
    if (workflowTurns.recovery !== "ready") return false;
    if (workflowAction && !workflowConversation) return false;

    if (
      (!toolApprovalResponse && !workflowAction && !rawText.trim() && attachedFiles.length === 0) ||
      isSubmitting ||
      streamAbortControllerRef.current ||
      !selectedAssistant ||
      !orgId ||
      selectedAssistant.id === "loading" ||
      !canSendMessages ||
      tokenLimitReachedRef.current
    )
      return false;

    if (isOptimisticConversation) return false;

    if (
      conversationId &&
      isAutoAssistantModel(selectedAssistant.model) &&
      !isAutoSelectableForConversation(conversationId)
    ) {
      setAlert({
        status: "error",
        message: t("autoUnavailableOnLegacyConversation"),
        secondsToClose: 10,
      });
      return false;
    }

    if (workflowConversation) workflowTurns.clearError();
    const files = toolApprovalResponse || workflowAction ? [] : attachedFiles;
    const approvalProposal = toolApprovalResponse ? messages.at(-1) : undefined;
    const expectedProposalMessageId = approvalProposal?.id;
    const toolApprovalCalls = approvalProposal?.contents.filter((content): content is MessageContentFunctionCall =>
      content.type === "function_call" && !!content.approval
    );
    const hasAttachments = files.length > 0;
    setIsSubmitting(true);
    if (workflowConversation && !(await workflowTurns.prepareTurn())) {
      setIsSubmitting(false);
      return false;
    }

    await runCancellableSend(async (signal) => {
      if (!conversationId) {
        throw new Error("No conversation ID available");
      }
      if (toolApprovalResponse && !expectedProposalMessageId) {
        throw new Error("An approval requires its proposal message ID.");
      }

      const { contents, uiMessages } = toolApprovalResponse
        ? {contents: [], uiMessages: []}
        : await buildSendPayload(textToSend, files, t);

      setMessages((prev) => [...prev, ...uiMessages]);
      if (!toolApprovalResponse && !workflowAction) {
        setInputValue("");
        clearAttachedFiles();
      }

      const routing = resolveRoutingForConversation(conversationId);
      const client = await getMessageClient({ hasAttachments, routing });

      const response = await client.conversations.appendConversationMessage(
        {
          xOrgId: orgId,
          conversationId: conversationId,
          appendConversationMessageRequest: {
            contents,
            ...(routing.apiVersion === "v2" ? {approvalPresentation: "native" as const} : {}),
            ...(toolApprovalResponse ? {toolApprovalResponse, expectedProposalMessageId} : {}),
            pageContext: resolvePageContext(),
            entitlementContext: resolveEntitlementContext(),
          },
        },
        { signal, ...(toolApprovalCalls ? {toolApprovalCalls} : {}) }
      );
      pinConversationRouting(conversationId, routing);
      captureWingmanConversationFlow({
        action: "send",
        conversationId,
        routing,
      });

      const interrupted = await processResponse(response, approvalProposal);
      if (workflowConversation && interrupted) await workflowTurns.reconcile();
    }, "regular", undefined, workflowConversation);
    return true;
  };

  /** See `sendMessage` for the meaning of the resolved boolean. */
  const handleSendMessage = (messageText?: string): Promise<boolean> => sendMessage({messageText});

  const canApproveTools = !!user?.userId && ownerConversationId === conversationId && conversationOwnerId === user.userId;

  const handleToolApprovalResponse = async (decision: ToolApprovalResponse) => {
    if (!canApproveTools || !conversationId || isSubmitting || streamAbortControllerRef.current || resolveRoutingForConversation(conversationId).apiVersion !== "v2") return;
    // Only the latest persisted proposal can be decided, one approval per request.
    const latest = messages.at(-1);
    if (latest?.role !== "assistant" || !latest.contents.some((content) =>
      content.type === "function_call" && content.approval?.id === decision.id &&
      content.approval.approved === undefined && !latest.contents.some((item) =>
        item.type === "function_result" && item.callId === content.callId)
    )) return;
    // Wingman's decision schema is strict: send exactly the approval ID and the decision.
    if (!(await sendMessage({toolApprovalResponse: {id: decision.id, approved: decision.approved}}))) throw new Error("Approval submission failed");
  };

  // Message processing context - memoized to avoid recreating on each render
  const messageProcessingContext = useMemo<MessageProcessingContext>(
    () => ({
      t,
      streamingMessageId,
      getFileExtensionFromMimeType,
    }),
    [t, streamingMessageId, getFileExtensionFromMimeType]
  );

  const convertToChatMessages = useCallback(
    (convMessages: ConversationMessage[]) =>
      groupApprovalContinuations(convMessages).flatMap((msg) =>
        convertSingleMessage(msg, streamingMessageId, messageProcessingContext).map(converted => ({
          ...converted, approvalMessageIds: msg.approvalMessageIds,
        }))
      ),
    [messageProcessingContext, streamingMessageId]
  );

  /** Set messages only if local state is empty (prevents overwriting in-flight user messages). */
  const setMessagesIfEmpty = (messages: ConversationMessage[]) => {
    setMessages((current) => (current.length > 0 ? current : messages));
  };

  const loadExistingConversation = async () => {
    if (!orgId || !conversationId) return;

    const maxRetries = 3;
    let retryCount = 0;

    const retryWithBackoff = async (): Promise<{
      conversation: ConversationDetailsResponse;
      messages: ConversationMessage[];
    }> => {
      try {
        const feedbackMapPromise = fetchFeedbackMap(conversationId);
        // Discovery can fail before feedback is awaited. Attach a rejection
        // handler now so that failure cannot become an unhandled promise.
        void feedbackMapPromise.catch(() => undefined);
        const { routing, value } = await discoverConversationRouting(
          conversationId,
          async (candidateRouting, init) => {
            const client = await getAuthenticatedClient(candidateRouting);
            const [conversationResult, messagesResult] = await Promise.allSettled([
              client.conversations.getConversation(
                {
                  xOrgId: orgId,
                  conversationId: conversationId,
                },
                { signal: init?.signal }
              ),
              client.conversations.listConversationMessages(
                {
                  xOrgId: orgId,
                  conversationId: conversationId,
                },
                { signal: init?.signal }
              ),
            ]);

            // Route discovery is based only on the canonical conversation
            // resource. A message-list failure must not masquerade as a 404
            // for the conversation and incorrectly switch deployments.
            if (conversationResult.status === "rejected") {
              throw conversationResult.reason;
            }
            if (messagesResult.status === "rejected") {
              const messageListError = messagesResult.reason;
              if (
                typeof messageListError === "object" &&
                messageListError !== null &&
                "status" in messageListError &&
                messageListError.status === 404
              ) {
                throw new Error("Conversation message list not found", {
                  cause: messageListError,
                });
              }
              throw messageListError;
            }
            return {
              conversation: conversationResult.value,
              wingmanMessages: messagesResult.value,
            };
          },
          {
            rank: ({ wingmanMessages }) =>
              rankConversationHistory({
                messageCount: wingmanMessages.length,
                lastTimestamp: wingmanMessages.at(-1)?.timestamp,
              }),
            model: selectedAssistant?.model,
          }
        );
        const { conversation, wingmanMessages } = value;
        const feedbackMap = await feedbackMapPromise;

        if (!conversation) {
          throw new Error("Conversation not found");
        }
        captureWingmanConversationFlow({
          action: "load",
          conversationId,
          routing,
        });

        const messages = (wingmanMessages ?? []).map((msg) => {
          const feedback = feedbackMap.get(msg.id);
          return feedback ? { ...msg, feedback } : msg;
        });

        if (conversation && messages.length > 0) {
          setMessagesIfEmpty(messages);
          if (conversation.userId) {
            setConversationOwnerId(conversation.userId);
            setOwnerConversationId(conversationId);
          }
          return { conversation, messages };
        }

        if (retryCount < maxRetries) {
          retryCount++;
          const delay = Math.pow(2, retryCount - 1) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
          return retryWithBackoff();
        }

        setMessagesIfEmpty(messages);
        return { conversation, messages };
      } catch (error) {
        console.error("Error loading conversation:", error);

        if (retryCount < maxRetries) {
          retryCount++;
          const delay = Math.pow(2, retryCount - 1) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delay));
          return retryWithBackoff();
        }

        throw error;
      }
    };

    return retryWithBackoff();
  };

  const getConversationDetails = async () => {
    if (!orgId || !conversationId) return null;

    try {
      const { value: conversation } = await discoverConversationRouting(
        conversationId,
        async (routing, init) => {
          const client = await getAuthenticatedClient(routing);
          return client.conversations.getConversation(
            {
              xOrgId: orgId,
              conversationId: conversationId,
            },
            { signal: init?.signal }
          );
        },
        { model: selectedAssistant?.model }
      );

      return conversation;
    } catch (error) {
      console.error("Error fetching conversation details:", error);

      throw error;
    }
  };

  const handleFeedback = async (
    messageId: string,
    rating: FeedbackRating | null,
    categories?: FeedbackCategory[],
    comment?: string
  ) => {
    if (!conversationId) return;

    // Store previous feedback for rollback
    const prevFeedback = messages.find((msg) => msg.id === messageId)?.feedback;

    // Optimistically update local state
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId
          ? { ...msg, feedback: rating ? { rating, categories, comment } : undefined }
          : msg
      )
    );

    try {
      if (rating) {
        await submitMessageFeedback(conversationId, messageId, { rating, categories, comment });
      } else {
        await deleteMessageFeedback(conversationId, messageId);
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      // Revert on failure
      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, feedback: prevFeedback } : msg))
      );
      setAlert({
        status: "error",
        message: t("feedback.saveFailed"),
        secondsToClose: 10,
      });
    }
  };

  // Reset function to clear submitting state and other relevant states
  const resetChatState = () => {
    // Abort any in-flight stream so its late events can't write into the
    // freshly reset state.
    streamAbortControllerRef.current?.abort();
    streamAbortControllerRef.current = null;
    setIsSubmitting(false);
    setStreamingMessageId(null);
    setIsOptimisticConversation(false);
    setIsWaitingForToolResponse(false);
    setTokenUsagePercent(0);
    setTokenBreakdown(EMPTY_BREAKDOWN);
    setTokenLimitReached(false);
    tokenLimitReachedRef.current = false;
  };

  return {
    messages,
    setMessages,
    inputValue,
    setInputValue,
    isSubmitting,
    streamingMessageId,
    fileCompatibilityIssue,
    selectedAssistant,
    isLoadingAssistants,
    displayAssistants,
    displaySelectedAssistant,
    handleAssistantChange,
    updateAssistantData,
    handleSendMessage,
    handleWorkflowTest: (workflowId: string) =>
      sendMessage({workflowAction: {test: {workflowId}}}),
    handleWorkflowDebug: (sessionId: string, action: WorkflowDebugAction) =>
      sendMessage({workflowAction: {debug: {sessionId, action}}}),
    workflowRecovery: workflowTurns.recovery,
    workflowError: workflowTurns.error,
    reconcileWorkflowHistory: workflowTurns.reconcile,
    workflowToolsAvailable: workflowTurns.available,
    workflowPresentation,
    handleToolApprovalResponse,
    canApproveTools,
    handleFilesSelected,
    handleFilesChange,
    handleInvalidFiles,
    cancelStream,
    attachedFiles,
    fileUploadConfig,
    convertToChatMessages,
    loadExistingConversation,
    getAuthenticatedClient,
    getConversationDetails,
    isOptimisticConversation,
    canSendMessages,
    conversationOwnerId,
    fetchAssistants,
    handleFeedback,
    resetChatState,
    isWaitingForToolResponse,
    streamingStatus,
    hasFetchError,
    tokenLimitReached,
    tokenUsagePercent,
    tokenBreakdown,
  };
};
